// src/ProceduresPage.js - VERSIONE CON DOWNLOAD/APERTURA FILE

import React, { useState, useEffect } from 'react';
import './ProceduresPage.css';
import { db, storage } from './firebase';
import { collection, getDocs, addDoc, deleteDoc, doc, updateDoc, serverTimestamp } from "firebase/firestore";
import { ProcedureModal, SimpleViewModal } from './Modals';
import { useUser } from './UserContext';
import { logAction } from './loggingService';
import { ref, uploadBytes, getDownloadURL, deleteObject } from "firebase/storage";
import { FaFilePdf, FaFileWord, FaFileExcel, FaFileAlt, FaDownload } from 'react-icons/fa';
import { toast } from 'react-toastify';
import { v4 as uuidv4 } from 'uuid';

const getFileIcon = (fileName) => {
    const extension = fileName.split('.').pop().toLowerCase();
    switch (extension) {
        case 'pdf': return <FaFilePdf style={{ color: '#dc3545' }} />;
        case 'doc':
        case 'docx': return <FaFileWord style={{ color: '#0d6efd' }} />;
        case 'xls':
        case 'xlsx': return <FaFileExcel style={{ color: '#198754' }} />;
        default: return <FaFileAlt style={{ color: '#6c757d' }} />;
    }
};

// Componente per visualizzare una singola procedura
const ProcedureCard = ({ procedure, categoryName, onEdit, onDelete, isExpanded, onToggleExpand, canEdit, canDelete, onViewContent }) => (
  <div className="procedure-card">
    <div 
        className="card-header" 
        onClick={() => onViewContent(procedure)} // Sempre cliccabile per aprire/scaricare
        style={{ cursor: 'pointer' }} // Mostra che è cliccabile
    >
      <h3>{procedure.title}</h3>

      <div className="header-right-group" onClick={(e) => e.stopPropagation()}>
        <span className="card-visibility-badge" title={
            procedure.visibility === 'private' ? 'Visibile solo a te' :
            procedure.visibility === 'shared' ? `Condiviso con ${procedure.sharedWith?.length || 0} utent${procedure.sharedWith?.length !== 1 ? 'i' : 'e'}` :
            'Visibile a tutti'
        }>
            {procedure.visibility === 'private' ? '🔒' : procedure.visibility === 'shared' ? '🤝' : '🌍'}
        </span>
        <span className="card-category">{categoryName}</span>

        {/* Icona file con tooltip che mostra "Clicca per aprire/scaricare" */}
        {procedure.fileUrl && (
             <span 
                className="card-visibility-badge" 
                title={`Clicca per aprire/scaricare: ${procedure.fileName}`}
                style={{ cursor: 'pointer' }}
             >
                 {getFileIcon(procedure.fileName || '')}
                 <FaDownload style={{ marginLeft: '5px', fontSize: '0.8em', color: '#28a745' }} />
             </span>
        )}
        
        {(canEdit || canDelete) && (
            <div className="card-actions">
                {canEdit && <button onClick={() => onEdit(procedure)} className="edit-btn">Modifica</button>}
                {canDelete && <button onClick={() => onDelete(procedure.id, procedure.fileUrl, procedure.storagePath)} className="delete-btn">Elimina</button>}
            </div>
        )}
      </div>
    </div>
    
    {/* CONTENUTO ESPANSO (solo per Rich Text) */}
    {!procedure.fileUrl ? (
        <div className={`card-content ${isExpanded ? 'open' : ''}`}>
             <span onClick={() => onViewContent(procedure)} style={{ cursor: 'pointer' }}>
                <div dangerouslySetInnerHTML={{ __html: procedure.content }} />
             </span>
        </div>
     ) : (
         // Per i file caricati, mostra info e pulsante download
         <div 
            className="card-content" 
            style={{ 
                maxHeight: 'none', 
                padding: '15px', 
                backgroundColor: '#f8f9fa',
                borderTop: '1px solid #dee2e6'
            }}
         >
             <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                {getFileIcon(procedure.fileName || '')}
                <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 'bold', color: '#0d6efd', marginBottom: '5px' }}>
                        {procedure.fileName || 'File Caricato'}
                    </div>
                    <div style={{ fontSize: '0.9em', color: '#6c757d' }}>
                        Clicca sull'intestazione per aprire/scaricare
                    </div>
                </div>
                <button 
                    onClick={() => onViewContent(procedure)} 
                    style={{
                        padding: '8px 16px',
                        backgroundColor: '#28a745',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '5px'
                    }}
                >
                    <FaDownload /> Apri
                </button>
             </div>
         </div>
     )}
  </div>
);

function ProceduresPage() {
    const { userProfile } = useUser(); 
    const permissionLevel = userProfile?.permissions?.procedures || 'none';
    const canCreate = permissionLevel === 'completo' || permissionLevel === 'semi-completo';
    const canEdit = permissionLevel === 'completo' || permissionLevel === 'semi-completo';
    const canDelete = permissionLevel === 'completo';

    const [categories, setCategories] = useState([]);
    const [newCategory, setNewCategory] = useState('');
    const [procedures, setProcedures] = useState([]);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingProcedure, setEditingProcedure] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedCategory, setSelectedCategory] = useState('all');
    const [filteredProcedures, setFilteredProcedures] = useState([]);
    const [isAccordionOpen, setIsAccordionOpen] = useState(false);
    const [expandedId, setExpandedId] = useState(null);
    const [staffList, setStaffList] = useState([]);
    const [isViewModalOpen, setIsViewModalOpen] = useState(false);
    const [viewModalData, setViewModalData] = useState({});

    const loadData = async () => {
        if (!userProfile || !userProfile.centerId) {
            setCategories([]);
            setProcedures([]);
            setStaffList([]);
            return;
        }
        const centerId = userProfile.centerId;

        // Carica Categorie
        const categoriesSnapshot = await getDocs(collection(db, 'centers', centerId, "categories"));
        const categoriesList = categoriesSnapshot.docs.map(doc => ({
            id: doc.id,
            name: doc.data().name
        })).sort((a, b) => a.name.localeCompare(b.name));
        setCategories(categoriesList);

        // Carica Staff
        const staffSnapshot = await getDocs(collection(db, 'centers', centerId, "staff"));
        const staff = staffSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        setStaffList(staff);

        // Carica Procedure
        const proceduresSnapshot = await getDocs(collection(db, 'centers', centerId, "procedures"));
        const proceduresList = proceduresSnapshot.docs.map(doc => {
            const data = doc.data();
            const category = categoriesList.find(c => c.id === data.categoryId);
            return {
                id: doc.id,
                ...data,
                categoryName: category ? category.name : "Senza categoria"
            };
        }).sort((a, b) => a.title.localeCompare(b.title));
        setProcedures(proceduresList);
    };

    useEffect(() => {
        loadData();
    }, [userProfile]);

    useEffect(() => {
        if (!userProfile) return;

        let result = procedures.filter(proc => {
            if (proc.visibility === 'private') {
                return proc.ownerId === userProfile.uid;
            }
            if (proc.visibility === 'shared') {
                return proc.ownerId === userProfile.uid || (proc.sharedWith && proc.sharedWith.includes(userProfile.uid));
            }
            return true;
        });

        if (selectedCategory !== 'all') {
            result = result.filter(proc => proc.categoryId === selectedCategory);
        }
        if (searchTerm.trim() !== '') {
            const lowerCaseSearchTerm = searchTerm.toLowerCase();
            result = result.filter(proc =>
                proc.title.toLowerCase().includes(lowerCaseSearchTerm) ||
                (proc.content && proc.content.toLowerCase().includes(lowerCaseSearchTerm))
            );
        }
        setFilteredProcedures(result);
    }, [searchTerm, selectedCategory, procedures, userProfile]);

    const handleAddCategory = async () => {
        if (newCategory.trim() === '') {
            alert("Il nome della categoria non può essere vuoto.");
            return;
        }
        await addDoc(collection(db, 'centers', userProfile.centerId, "categories"), { 
            name: newCategory.trim(), 
            centerId: userProfile.centerId 
        });
        await logAction(userProfile, `ha creato la categoria: ${newCategory.trim()}`);
        setNewCategory('');
        loadData();
    };

    const handleDeleteCategory = async (categoryId) => {
        const categoryToDelete = categories.find(c => c.id === categoryId);
        if (window.confirm("Sei sicuro di voler eliminare questa categoria?")) {
            await deleteDoc(doc(db, 'centers', userProfile.centerId, "categories", categoryId));
            if(categoryToDelete) {
                await logAction(userProfile, `ha eliminato la categoria: ${categoryToDelete.name}`);
            }
            loadData();
        }
    };
    
    const handleOpenNewProcedureModal = () => {
        setEditingProcedure(null);
        setIsModalOpen(true);
    };

    const handleEditProcedure = (procedure) => {
        setEditingProcedure(procedure);
        setIsModalOpen(true);
    };

    const handleSaveProcedure = async (procedureData) => {
        // --- MODIFICA: Estrai 'hideOnMobile' dai dati ---
        const { selectedFile, existingFileUrl, hideOnMobile, ...data } = procedureData;
        let fileUrl = existingFileUrl;
        let fileName = data.fileName || '';
        let storagePath = data.storagePath || '';

        if (selectedFile) {
            const fileExtension = selectedFile.name.split('.').pop();
            const uniqueFileName = `${data.centerId}_${uuidv4()}.${fileExtension}`;
            const newStoragePath = `procedures/${data.centerId}/${uniqueFileName}`;
            const fileRef = ref(storage, newStoragePath);

            try {
                if (existingFileUrl && data.storagePath) {
                    const oldFileRef = ref(storage, data.storagePath);
                    await deleteObject(oldFileRef).catch(err => console.warn("Errore eliminazione vecchio file:", err));
                }

                const snapshot = await uploadBytes(fileRef, selectedFile);
                fileUrl = await getDownloadURL(snapshot.ref);
                fileName = selectedFile.name;
                storagePath = newStoragePath;
            } catch (error) {
                toast.error("Errore nel caricamento del file.");
                console.error("Errore upload file:", error);
                return;
            }
        } else if (!data.content && !existingFileUrl) {
            fileUrl = ''; fileName = ''; storagePath = '';
        }
        
        const dataToSave = {
            ...data,
            content: data.content,
            fileUrl: fileUrl,
            fileName: fileName,
            storagePath: storagePath,
            hideOnMobile: hideOnMobile || false, // <-- AGGIUNGI IL NUOVO FLAG
        };
        
        if (!fileUrl) {
             delete dataToSave.fileUrl;
             delete dataToSave.fileName;
             delete dataToSave.storagePath;
        }

        if (procedureData.id) {
            const procedureRef = doc(db, 'centers', userProfile.centerId, "procedures", procedureData.id);
            await updateDoc(procedureRef, dataToSave);
            await logAction(userProfile, `ha modificato il documento: ${procedureData.title}`);
            toast.success("Documento aggiornato!");
        } else {
            const { id, ...newDocData } = dataToSave; 
            await addDoc(collection(db, 'centers', userProfile.centerId, "procedures"), {
                ...newDocData,
                createdAt: serverTimestamp() 
            });
            await logAction(userProfile, `ha creato il documento: ${dataToSave.title}`);
            toast.success("Documento creato!");
        }
        setIsModalOpen(false);
        loadData(); // loadData è già presente e ricaricherà i dati
    };

    const handleDeleteProcedure = async (procedureId, fileUrl, storagePath) => {
        if (window.confirm("Sei sicuro di voler eliminare questa procedura? Verrà eliminato anche il file associato.")) {
            try {
                await deleteDoc(doc(db, 'centers', userProfile.centerId, "procedures", procedureId));
                
                if (fileUrl && storagePath) {
                    const fileRef = ref(storage, storagePath);
                    await deleteObject(fileRef).catch(err => console.warn("File non trovato in Storage:", err));
                }
                
                await logAction(userProfile, `ha eliminato il documento: ${procedureId}`);
                loadData();
            } catch (error) {
                console.error("Errore eliminazione procedura:", error);
                toast.error("Errore durante l'eliminazione della procedura.");
            }
        }
    };

    const handleToggleExpand = (procedureId) => {
        setExpandedId(prevId => prevId === procedureId ? null : procedureId);
    };
    
    // 🔥 FUNZIONE MODIFICATA per gestire file e contenuto RTF
    const handleViewContent = (procedure) => {
        // Se è un file caricato, aprilo in una nuova tab
        if (procedure.fileUrl) {
            window.open(procedure.fileUrl, '_blank');
            logAction(userProfile, `ha visualizzato/scaricato il file: ${procedure.title}`);
            toast.info(`Apertura file: ${procedure.fileName}`);
        } else {
            // Altrimenti apri la modal per il contenuto RTF
            setViewModalData(procedure);
            setIsViewModalOpen(true);
        }
    };

    return (
    <>
        {isModalOpen && (
            <ProcedureModal
                procedureData={editingProcedure}
                categories={categories}
                onSave={handleSaveProcedure}
                onClose={() => setIsModalOpen(false)}
                staffList={staffList}
            />
        )}
        {isViewModalOpen && (
            <SimpleViewModal 
                modalData={viewModalData} 
                onClose={() => setIsViewModalOpen(false)} 
            />
        )}
        <div className="procedures-container">
            {canCreate && (
                <aside className="sidebar desktop-only">
                    <h3>Gestisci Categorie</h3>
                <div className="category-input-container">
                    <input type="text" value={newCategory} onChange={(e) => setNewCategory(e.target.value)} placeholder="Nuova categoria..."/>
                    <button onClick={handleAddCategory}>Aggiungi</button>
                </div>
                <ul className="category-list">
                    {categories.map(category => (
                        <li key={category.id} className="category-list-item">
                            <span>{category.name}</span>
                            <button onClick={() => handleDeleteCategory(category.id)} className="delete-category-btn" title="Elimina categoria">X</button>
                        </li>
                    ))}
                </ul>
            </aside>
             )}

            <div className="procedures-main-column">
                {canCreate && (
                <div className="accordion mobile-only">
                    <button className={`accordion-toggle ${isAccordionOpen ? 'open' : ''}`} onClick={() => setIsAccordionOpen(!isAccordionOpen)}>
                        Gestisci Categorie {isAccordionOpen ? '▲' : '▼'}
                    </button>
                    <div className={`accordion-content ${isAccordionOpen ? 'open' : ''}`}>
                         <div className="category-input-container">
                            <input type="text" value={newCategory} onChange={(e) => setNewCategory(e.target.value)} placeholder="Nuova categoria..."/>
                            <button onClick={handleAddCategory}>Aggiungi</button>
                        </div>
                        <ul className="category-list">
                            {categories.map(category => (
                                <li key={category.id} className="category-list-item">
                                    <span>{category.name}</span>
                                    <button onClick={() => handleDeleteCategory(category.id)} className="delete-category-btn" title="Elimina categoria">X</button>
                                </li>
                            ))}
                        </ul>
                    </div>
                </div>
                )}

                <main className="main-content">
                    <div className="main-content-header">
                        <h2>Documenti</h2>
                        {canCreate && (
                            <button onClick={handleOpenNewProcedureModal} className="new-procedure-btn">+ Nuovo Documento</button>
                        )}
                    </div>

                    <div className="filters-bar">
                        <input
                            type="text"
                            placeholder="Cerca per titolo o contenuto..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                        <select
                            value={selectedCategory}
                            onChange={(e) => setSelectedCategory(e.target.value)}
                        >
                            <option value="all">Tutte le categorie</option>
                            {categories.map(cat => (
                                <option key={cat.id} value={cat.id}>{cat.name}</option>
                            ))}
                        </select>
                    </div>

                    <div className="procedures-list">
                        {filteredProcedures.length > 0 ? (
                            filteredProcedures.map(proc => (
                                <ProcedureCard 
                                    key={proc.id} 
                                    procedure={proc} 
                                    categoryName={proc.categoryName}
                                    onEdit={handleEditProcedure}
                                    onDelete={handleDeleteProcedure}
                                    isExpanded={expandedId === proc.id && !proc.fileUrl}
                                    onToggleExpand={() => handleToggleExpand(proc.id)}
                                    onViewContent={handleViewContent}
                                    canEdit={canEdit}  
                                    canDelete={canDelete} 
                                />
                            ))
                        ) : (
                            <p>Nessun documento trovato con i filtri attuali.</p>
                        )}
                    </div>
                </main>
            </div>
        </div>
    </>
);
}

export default ProceduresPage;